/**
 * "Add property" mutation for the Properties page.
 *
 * Adds an already-owned property by hand, mirroring the multi-table write the
 * Closed-Won flow performs (see `moveToClosedWonMutation`) but starting from an
 * empty row instead of an existing pipeline deal:
 *
 *   1. INSERT pipeline_properties  — is_owned=true + synthesized property_data,
 *      landed in the workspace's Closed-Won pipeline when one exists.
 *   2. INSERT property_setting     — headline inputs + the strategy loan blob.
 *   3. INSERT loans                — one enabled row; loan_type selects the slot.
 *   4. INSERT rehab_projects       — only when the user marks it "In Rehab".
 *
 * Supabase has no client-side transaction, so the steps run sequentially and a
 * later failure throws (surfaced via toast) — matching the app's existing
 * acquisition write path.
 */
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PostgrestSingleResponse } from "@supabase/supabase-js";
import { format } from "date-fns";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useWorkspace } from "@/hooks/use-workspace";
import {
  buildPropertyData,
  buildStrategyPatch,
  PROPERTY_IMAGES_BUCKET,
  propertyImagePath,
  resolveArv,
  resolveLoanType,
  ZERO_CASHFLOW,
  type AddPropertyForm,
} from "@/lib/add-property";
import { resolveOwnedDate } from "@/lib/closed-won";
import { findClosedWonPipeline } from "@/lib/pipeline-display";
import { splitStrategySettings } from "@/lib/property-setting";
import { toUserMessage } from "@/lib/supabase-errors";
import type { PipelineRow } from "@/types/pipeline";

type DbResp<T> = Promise<PostgrestSingleResponse<T>>;

/** Wizard payload: the flat form model plus an optional cover photo to upload. */
export interface AddPropertyInput {
  form: AddPropertyForm;
  /** Selected file to upload after the property exists; null → URL/none. */
  imageFile?: File | null;
}

/** Generate a stable, unique `property_id` for a hand-added property. */
function newPropertyId(): string {
  return `manual-${crypto.randomUUID()}`;
}

export function useAddProperty() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();
  const workspaceId = currentWorkspace?.id;

  // Shares the cache key used by usePipeline / useSavedProperties so the
  // pipelines list is fetched once per workspace.
  const pipelinesQuery = useQuery({
    queryKey: ["pipelines", workspaceId] as const,
    enabled: Boolean(workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as PipelineRow[];
      const { data, error } = (await (
        supabase as unknown as {
          from: (t: string) => {
            select: (s: string) => {
              eq: (col: string, v: string) => {
                order: (col: string) => DbResp<PipelineRow[]>;
              };
            };
          };
        }
      )
        .from("pipelines")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("order")) as PostgrestSingleResponse<PipelineRow[]>;
      if (error) throw error;
      return data ?? [];
    },
  });

  const closedWon = useMemo(
    () => findClosedWonPipeline(pipelinesQuery.data ?? []),
    [pipelinesQuery.data],
  );

  const mutation = useMutation({
    mutationFn: async ({ form, imageFile }: AddPropertyInput): Promise<string> => {
      if (!user?.id || !workspaceId) {
        throw new Error("Please log in and select a workspace to add a property");
      }

      const today = format(new Date(), "yyyy-MM-dd");
      const ownedDate = resolveOwnedDate(form.purchaseDate, today);
      const propertyId = newPropertyId();
      const propertyData = buildPropertyData(form, propertyId);

      // 1. pipeline_properties — mark owned, land in Closed-Won when present.
      const { data: inserted, error: ppErr } = (await supabase
        .from("pipeline_properties")
        .insert({
          created_by: user.id,
          workspace_id: workspaceId,
          property_id: propertyId,
          property_data: propertyData as unknown as Record<string, unknown>,
          purchase_price: form.purchase,
          strategy: form.strategy,
          pipeline_id: closedWon?.id ?? null,
          is_owned: true,
          owned_date: ownedDate,
        } as never)
        .select("id")
        .single()) as PostgrestSingleResponse<{ id: string }>;
      if (ppErr) throw ppErr;
      const pipelinePropertyId = inserted?.id;
      if (!pipelinePropertyId) {
        throw new Error("Failed to create the property record");
      }

      // 1b. Upload the cover photo (best-effort) now that we have the id to
      // fold it into the folder path, then patch property_data.imageUrl. A
      // failed upload never blocks creation — the photo can be added later.
      if (imageFile) {
        const path = propertyImagePath(pipelinePropertyId, imageFile.name, Date.now());
        const { error: uploadErr } = await supabase.storage
          .from(PROPERTY_IMAGES_BUCKET)
          .upload(path, imageFile, { upsert: true });
        if (uploadErr) {
          console.error("property image upload:", uploadErr);
        } else {
          const { data: urlData } = supabase.storage
            .from(PROPERTY_IMAGES_BUCKET)
            .getPublicUrl(path);
          const { error: imgErr } = await supabase
            .from("pipeline_properties")
            .update({
              property_data: {
                ...propertyData,
                imageUrl: urlData.publicUrl,
              } as unknown as Record<string, unknown>,
            } as never)
            .eq("id", pipelinePropertyId);
          if (imgErr) console.error("property image url patch:", imgErr);
        }
      }

      // Audit log (best-effort) — mirrors the acquisition flow.
      const { error: histErr } = await supabase
        .from("pipeline_stage_history")
        .insert({
          pipeline_property_id: pipelinePropertyId,
          changed_by: user.id,
          from_pipeline_id: null,
          to_pipeline_id: closedWon?.id ?? null,
          workspace_id: workspaceId,
        } as never);
      if (histErr) console.error("pipeline_stage_history insert:", histErr);

      // 2. property_setting — headline inputs + strategy loan blob.
      // Cast at the boundary: the generated Database type doesn't yet include
      // the new property_setting shape (same pattern as useDealSettings).
      const psClient = supabase as unknown as {
        from: (t: string) => { insert: (v: unknown) => DbResp<unknown> };
      };
      const { error: psErr } = await psClient.from("property_setting").insert({
        pipeline_property_id: pipelinePropertyId,
        workspace_id: workspaceId,
        created_by: user.id,
        purchase_price: form.purchase,
        monthly_rent: form.monthlyRent > 0 ? form.monthlyRent : null,
        repair_cost: form.rehabCost > 0 ? form.rehabCost : null,
        after_repair_value: resolveArv(form),
        // Explicit per-line zeros rather than `{}` — see ZERO_CASHFLOW.
        monthly_cash_flow: ZERO_CASHFLOW,
        ...splitStrategySettings(buildStrategyPatch(form)),
      });
      if (psErr) throw psErr;

      // 3. loans — the calculation source of truth (one enabled row).
      const { error: loanErr } = await supabase.from("loans").insert({
        workspace_id: workspaceId,
        pipeline_property_id: pipelinePropertyId,
        created_by_user_id: user.id,
        loan_type: resolveLoanType(form.strategy, form.brrrrLoanKind),
        status: "pending",
        is_enabled: true,
      } as never);
      if (loanErr) throw loanErr;

      // 4. rehab_projects — started, when the user marks it In Rehab.
      if (form.status === "in-rehab") {
        const { error: rehabErr } = await supabase
          .from("rehab_projects")
          .insert({
            workspace_id: workspaceId,
            pipeline_property_id: pipelinePropertyId,
            created_by_user_id: user.id,
            in_rehab: true,
            stage: "in_progress",
            start_date: ownedDate,
          } as never);
        if (rehabErr) throw rehabErr;
      }

      return pipelinePropertyId;
    },
    onSuccess: () => {
      toast.success("Property added");
      void queryClient.invalidateQueries({
        queryKey: ["pipeline_properties", workspaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["pipeline_properties_owned", workspaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["property_settings_owned", workspaceId],
      });
      void queryClient.invalidateQueries({ queryKey: ["loans_owned", workspaceId] });
      void queryClient.invalidateQueries({
        queryKey: ["rehab_projects_owned", workspaceId],
      });
    },
    onError: (err: unknown) => {
      toast.error(toUserMessage(err, "Failed to add property"));
    },
  });

  return { addProperty: mutation };
}
